/**
 * Client-side username profanity filter for The Orbital Ledger.
 * PRD-079 Step 1.5 — provides instant feedback during registration.
 *
 * IMPORTANT: This is a client-side copy for UX purposes only.
 * The server-side filter (src/lib/usernameFilter.js) is the authoritative gate.
 * Keep both files in sync when updating the word list.
 */

const RESERVED_NAMES = [
  'admin', 'administrator', 'moderator', 'mod', 'owner',
  'system', 'support', 'help', 'root', 'sysadmin',
  'superuser', 'theverse', 'verseledger', 'theverseledger',
  'theorbital', 'orbitalledger', 'theorbitalledger',
];

const BLOCKED_TERMS = [
  // Racial/ethnic slurs
  'nigger', 'nigga', 'coon', 'darkie', 'spic', 'spick', 'wetback', 'beaner',
  'chink', 'gook', 'zipperhead', 'slanteye', 'paki', 'raghead', 'towelhead',
  'sandnigger', 'cameljockey', 'kike', 'heeb', 'hymie', 'jewboy',
  'redskin', 'injun', 'squaw', 'halfbreed', 'chinaman', 'wop', 'dago',
  'polack', 'kraut', 'jiggaboo', 'sambo', 'pickaninny', 'porchmonkey', 'tarbaby',
  // Homophobic/transphobic slurs
  'faggot', 'faggy', 'dyke', 'lesbo', 'tranny', 'shemale', 'ladyboy',
  'heshe', 'sodomite', 'cocksucker', 'buttfucker',
  // Sexist slurs
  'cunt', 'whore', 'slut', 'skank', 'twat', 'thot',
  // Explicit sexual terms
  'fuck', 'fucker', 'motherfucker', 'shit', 'shitter', 'asshole', 'arsehole',
  'pussy', 'blowjob', 'handjob', 'cumshot', 'jizz', 'dildo', 'wanker',
  'masturbat', 'rape', 'rapist',
  // Hate/extreme
  'nazi', 'hitler', 'heil', 'siegheil', 'kkk', 'whitesupremac', 'whitepower',
  'gasthejews', 'genocide', 'pedophile', 'pedo', 'paedo', 'molest',
  'childporn', 'lolicon', 'shotacon', 'necrophil', 'bestiality',
  'retard', 'retarded', 'spaz', 'spastic',
];

function normalize(raw) {
  let s = raw.toLowerCase();
  s = s.replace(/[013457@$!|(+]/g, (ch) => {
    const map = { '0':'o', '1':'i', '3':'e', '4':'a', '5':'s', '7':'t', '@':'a', '$':'s', '!':'i', '(':'c', '+':'t', '|':'l' };
    return map[ch] || ch;
  });
  s = s.replace(/ph/g, 'f');
  s = s.replace(/[_\-.\s\u200B\u200C\u200D\uFEFF]/g, '');
  s = s.replace(/(.)\1{1,}/g, '$1');
  return s;
}

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const NORMALIZED_BLOCKED = BLOCKED_TERMS.map((term) => normalize(term));

export function isUsernameAllowed(username) {
  if (!username || typeof username !== 'string') {
    return { allowed: false, reason: 'Username is required.' };
  }

  const raw = username.toLowerCase();
  const stripped = raw.replace(/[_\-.\s]/g, '');
  const normalized = normalize(username);

  if (RESERVED_NAMES.includes(normalized) || RESERVED_NAMES.includes(stripped)) {
    return { allowed: false, reason: 'This username is reserved. Please choose a different name.' };
  }

  const NOT_ALLOWED = { allowed: false, reason: 'This username is not allowed. Please choose a different name.' };

  for (let i = 0; i < NORMALIZED_BLOCKED.length; i++) {
    const term = NORMALIZED_BLOCKED[i];
    if (term.length <= 2) {
      if (normalized === term || stripped === term) return NOT_ALLOWED;
      continue;
    }
    if (term.length === 3) {
      const shortPattern = new RegExp('(?:^|[^a-z])' + escapeRegex(term) + '(?:[^a-z]|$)');
      if (normalized === term || stripped === term || shortPattern.test(stripped) || shortPattern.test(normalized)) return NOT_ALLOWED;
      continue;
    }
    if (stripped.includes(term) || normalized.includes(term)) return NOT_ALLOWED;
  }

  return { allowed: true };
}

export { RESERVED_NAMES };
